import { useSelector } from 'react-redux';
import { useUserData } from './useUserData';
import { RootState } from 'redux/store';
import { useEffect, useMemo, useState } from 'react';
import { GenerateDataTranslation, mappingLocale } from 'utils/translation';
import UserService from 'services/userServices';
import { validateEmail } from 'utils/function';
import { UserInfo } from 'models';

type Options = {
  checkCurrentEmail?: boolean;
  isManagerEmail?: boolean;
  isCorporateEmail?: boolean;
}

export const useCheckEmail = (options: Options = {}, localeConfig: any = {}) => {
  const currentUser = useUserData();
  const { generalConfig = {} } = useSelector((state: RootState) => state.configReducer);
  const { queryUserByUserName = false, checkEmail } = generalConfig;

  const [loading, setLoading] = useState(false);
  const [acceptedEmail, setAcceptedEmail] = useState<string[]>([]);
  const [validationMessage, setValidationMessage] = useState<Record<string, string>>({});

  const checkEmailTranslation = GenerateDataTranslation('checkEmail');

  const locale = useMemo(() => {
    return mappingLocale(localeConfig?.baseComponent?.checkEmail, checkEmailTranslation);
  }, [localeConfig?.baseComponent?.checkEmail, checkEmailTranslation]);

  useEffect(() => {
    (async () => {
      setAcceptedEmail(checkEmail?.ACCEPTED_EMAIL_DOMAINS);
      const key = 'VALIDATION_MESSAGES';
      const validationMessage = locale?.[key] || {};
      setValidationMessage(validationMessage);
    })();
  }, [checkEmail, locale]);

  const getUserDetail = (email: string) => {
    if (queryUserByUserName) {
      return UserService.getUserDetailByUserName(email);
    }
    return UserService.getUserDetailByEmail(email);
  };

  const onCheckEmail = async (valueEmail: string): Promise<[string[], UserInfo | Object | null]> => {
    valueEmail = valueEmail.toLowerCase();
    const { checkCurrentEmail, isManagerEmail, isCorporateEmail = true } = options || {};

    const isCurrentUser = currentUser?.email === valueEmail && checkCurrentEmail;
    if (isCurrentUser) {
      const errors = [validationMessage.SAME_USER];
      return [errors, {}];
    }

    const [errorCode, isValid] = validateEmail(valueEmail, { acceptedEmail: isCorporateEmail ? acceptedEmail : [] });
    if (!isValid) {
      const errors = [validationMessage[errorCode]];
      return [errors, {}];
    }

    if (!isCorporateEmail) return [[], ({ email: valueEmail } as UserInfo)];

    setLoading(true);
    const response = await getUserDetail(valueEmail);
    setLoading(false);

    const user = response?.data?.[0];

    if (!user) {
      const errors = [validationMessage.USER_NOT_FOUND];
      return [errors, null];
    }

    if (isManagerEmail && !user.isManager) {
      const errors = [validationMessage.USER_NOT_MANAGER];
      return [errors, null];
    }

    return [[], user];
  };

  return { loading, onCheckEmail, locale };
};